import React from "react";
import { motion } from "framer-motion";
import { useGuidedTour } from "@/lib/guidedTour";

// Beat 01 canvas — centered headline + subtitle, with the 6-beat rail beneath.
export default function ExchangeHero({ name, accent, beats, logoUrl }) {
  const { isActive, step } = useGuidedTour();
  return (
    <div data-testid="exchange-canvas" className="relative mt-6 rounded-3xl border border-[#D4AF37]/20 bg-[#0A0612] overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <div aria-hidden className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[min(620px,100%)] aspect-square rounded-full blur-3xl opacity-30"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 65%)` }} />

      <div className="relative min-h-[56vh] flex flex-col items-center justify-center text-center px-6 py-10">
        <motion.div
          data-testid="exchange-beat01-hero"
          initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="flex flex-col items-center px-6 py-6"
        >
          {logoUrl && (
            <motion.img src={logoUrl} alt="" data-testid="exchange-hero-medallion"
              initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 140, damping: 16 }}
              className="mb-7 w-24 h-24 sm:w-28 sm:h-28 object-contain" style={{ filter: `drop-shadow(0 0 28px ${accent}55)` }} />
          )}
          <h1 data-testid="exchange-headline" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] bg-gradient-to-b from-white via-[#FAFAF7] to-[#F4D88A] bg-clip-text text-transparent">
            {name}
          </h1>
          <motion.p
            data-testid="exchange-subtitle"
            initial={{ opacity: 0, letterSpacing: "0.6em" }} animate={{ opacity: 1, letterSpacing: "0.32em" }} transition={{ delay: 0.6, duration: 0.9 }}
            className="mt-5 font-mono text-xs sm:text-sm uppercase"
            style={{ color: accent }}
          >
            UCMS Life Pass Platform
          </motion.p>
        </motion.div>

        <div data-testid="exchange-beat-rail" className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {beats.map((b, i) => {
            const on = isActive && step === i;
            return (
              <div key={b.id} data-testid={i === 0 ? "exchange-beat-tile-01" : `exchange-beat-tile-${b.step}`}
                className="px-3 py-1.5 rounded-full border font-mono text-[10px] tracking-[0.16em] uppercase transition-colors duration-300"
                style={on ? { borderColor: accent, color: "#fff", background: `${accent}26` } : { borderColor: "rgba(255,255,255,0.1)", color: b.silent ? "#64748b" : "#cbd5e1" }}>
                {`${String(i + 1).padStart(2, "0")} · ${b.silent ? "Soon" : "Live"}`}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
